// SubscriptionModal.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaTimes, FaTrash, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { CategoryApiService } from '../../../../service/website/categoryApiService';
import { SubscriptionApiService } from '../../../../service/website/subscriptionApiService';
import { ProductsApiService } from '../../../../service/website/productsApiService';
import { fetchToken } from '../../../../lib/globalApiClient';
import CategoryDropdown from '../CategoryDropdown';
import { removeProductFromStorage } from '../../../helper/selectedProductsStorage';
import { normalizeUrl, appendSizeToImageUrl } from '../../../../utils/imageUtils';
import Logo from '../../../../assets/images/common/importerr-no-image-logo.png';
import './style.css';

const SubscriptionModal = ({
  show,
  onHide,
  selectedProductsByKeyword = {},
  onSubscribe,
  onProductRemove,
  isLoading = false
}) => {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [keywordCategories, setKeywordCategories] = useState({}); // { keyword: { categoryId, categoryName } }
  const [localProductsByKeyword, setLocalProductsByKeyword] = useState({});
  const [allCategories, setAllCategories] = useState([]); // Store all categories for autocomplete
  const [categoryCache, setCategoryCache] = useState({}); // Cache for search results
  const [skippedKeywords, setSkippedKeywords] = useState(new Set()); // Track keywords with skipped categories

  // Active tab
  const [activeTabKeyword, setActiveTabKeyword] = useState('');
  // NEW: local submit state (UX feedback)
  const [submitting, setSubmitting] = useState(false);
  // progress text for description gen
  const [descriptionProgress, setDescriptionProgress] = useState({ current: 0, total: 0 });

  // browse modal ui state
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState('');
  const [browseRoot, setBrowseRoot] = useState([]);
  const [browseCurrent, setBrowseCurrent] = useState([]);
  const [browseStack, setBrowseStack] = useState([]);
  const [browsePath, setBrowsePath] = useState([]);
  // per-SKU backend meta
  const [skuMeta, setSkuMeta] = useState({});

  const scrollerRef = useRef(null);

  // --- NEW: search normalization helpers ---
  const normalizeText = (s = '') =>
    s
      .toLowerCase()
      .normalize('NFKD').replace(/[\u0300-\u036f]/g, '') // strip diacritics
      .replace(/[>]/g, ' ')                               // '>' to space
      .replace(/[^a-z0-9\s]/g, ' ')                       // punctuation to space
      .replace(/\s+/g, ' ')
      .trim();

  const tokenize = (s = '') => normalizeText(s).split(' ').filter(Boolean);

  const uniqueById = (arr = []) => {
    const seen = new Set();
    return arr.filter(it => {
      const id = it?.id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  // Derived list of keywords
  const keywordGroups = useMemo(
    () => Object.keys(localProductsByKeyword),
    [localProductsByKeyword]
  );

  // Sync local state
  useEffect(() => {
    setLocalProductsByKeyword({ ...selectedProductsByKeyword });
  }, [selectedProductsByKeyword, show]);

  // fetch sku meta
  useEffect(() => {
    if (!show) return;

    const allSkus = [];
    Object.values(localProductsByKeyword).forEach(list => {
      (list || []).forEach(p => {
        const id = p.sku || p.offerId;
        if (id && !allSkus.includes(id)) {
          allSkus.push(id);
        }
      });
    });

    const skusToFetch = allSkus.filter(sku => !skuMeta[sku]);
    if (skusToFetch.length === 0) return;

    skusToFetch.forEach(async (sku) => {
      try {
        const res = await ProductsApiService.getSkuCategoryStatus(sku);

        if (res?.status?.code === 200 && res.data) {
          setSkuMeta(prev => ({
            ...prev,
            [sku]: {
              is_category_approved: res.data.is_category_approved === true,
              categoryPath: res.data.categoryPath || '',
              categoryId: res.data.categoryId || null,
            },
          }));
        } else {
          setSkuMeta(prev => ({
            ...prev,
            [sku]: {
              is_category_approved: false,
              categoryPath: '',
              categoryId: null,
            },
          }));
        }
      } catch (err) {
        console.error('Error fetching SKU category status:', sku, err);
        setSkuMeta(prev => ({
          ...prev,
          [sku]: {
            is_category_approved: false,
            categoryPath: '',
            categoryId: null,
          },
        }));
      }
    });
  }, [show, localProductsByKeyword, skuMeta]);

  const getDisplayPriceForProduct = (product) => {
    if (product?.priceRangeDisplay) return product.priceRangeDisplay;
    const base = product.finalPrice ? product.finalPrice :
      (product?.sp && Number(product.sp) > 0 && Number(product.sp)) ||
      (product?.consign_price && Number(product.consign_price) > 0 && Number(product.consign_price)) ||
      0;
    return base > 0 ? `₹${base.toFixed(2)}` : null;
  };

  const getVariantCount = (product) => {
    if (Array.isArray(product?.detailed_info?.productSkuInfos)) return product.detailed_info.productSkuInfos.length;
    if (product?.variationsCount) return product.variationsCount;
    if (Array.isArray(product?.variations)) return product.variations.length;
    return null;
  };

  const getDsFinalPriceNumber = (product) => {
    const n = product.finalPrice ||
      (product?.sp && Number(product.sp)) ||
      (product?.consign_price && Number(product.consign_price)) ||
      (product?.priceInfo?.consignPrice && Number(product.priceInfo.consignPrice)) ||
      (product?.price && Number(product.price)) ||
      null;
    return (Number.isFinite(n) && n > 0) ? Number(n) : null;
  };

  useEffect(() => {
    if (show && allCategories.length === 0) {
      loadAllCategories();
    }
    if (show) {
      setKeywordCategories({});
      setCategories([]);
    }
  }, [show]); // eslint-disable-line

  // Default active tab
  useEffect(() => {
    if (show) {
      if (keywordGroups.length > 0) {
        if (!activeTabKeyword || !keywordGroups.includes(activeTabKeyword)) {
          setActiveTabKeyword(keywordGroups[0]);
        }
      } else {
        setActiveTabKeyword('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, keywordGroups.join('|')]);

  // Reset on close
  useEffect(() => {
    if (!show) {
      setKeywordCategories({});
      setCategories([]);
      setLoadingCategories(false);
      setCategoryCache({});
      setActiveTabKeyword('');
      setSubmitting(false); // reset if modal closed while loading
      setDescriptionProgress({ current: 0, total: 0 });
      setSkippedKeywords(new Set()); // Reset skipped keywords
    }
  }, [show]);

  const isSkuApproved = useCallback(
    (skuId) => {
      return skuMeta[skuId]?.is_category_approved === true;
    },
    [skuMeta]
  );

  const isKeywordFullyApproved = useCallback(
    (keyword) => {
      const items = localProductsByKeyword[keyword] || [];
      if (items.length === 0) return false;
      return items.every(p => {
        const id = p.sku || p.offerId;
        return id && isSkuApproved(id);
      });
    },
    [localProductsByKeyword, isSkuApproved]
  );

  const loadAllCategories = async () => {
    try {
      const response = await CategoryApiService.getCategories();
      if (response.status?.code === 200 && response.data) {
        const flatCategories = [];
        const flattenCategories = (cats, parentPath = '') => {
          for (const category of cats) {
            const categoryId = category.c_id || category._id || category.id;
            const categoryName = category.c_name || category.name || category.categoryName;
            if (!categoryId || !categoryName) continue;

            const categoryPath = parentPath ? `${parentPath} > ${categoryName}` : categoryName;
            flatCategories.push({
              id: categoryId,
              name: categoryName,
              path: categoryPath,
              searchText: normalizeText(categoryPath)
            });

            const children = category.children || category.subCategories || category.subcategories;
            if (children && children.length > 0) flattenCategories(children, categoryPath);
          }
        };
        flattenCategories(response.data);
        setAllCategories(flatCategories);
      }
    } catch (e) {
      console.error('Error loading all categories:', e);
    }
  };

  // ---------- BROWSE MODAL: open and load full tree (fallback) ----------
  const openBrowse = async () => {
    try {
      setBrowseError('');
      setBrowseLoading(true);
      const res = await CategoryApiService.getCategories();
      if (res?.status?.code === 200 && Array.isArray(res.data)) {
        setBrowseRoot(res.data);
        setBrowseCurrent(res.data);
        setBrowseStack([]);
        setBrowsePath([]);
        setBrowseOpen(true);
      } else {
        setBrowseError('Unable to load categories');
      }
    } catch (e) {
      setBrowseError('Server error while loading categories');
    } finally {
      setBrowseLoading(false);
    }
  };

  // Pick common id/name/children from node
  const pickNodeShape = (node) => {
    return {
      id: node?.c_id || node?._id || node?.id,
      name: node?.c_name || node?.name || node?.categoryName,
      children: node?.children || node?.subCategories || node?.subcategories || []
    };
  };

  // Build full path text from current browsePath + clicked name
  const buildFullPath = (leafName) => {
    const parts = [...browsePath, leafName].filter(Boolean);
    return parts.join(' > ');
  };

  // Enter a node (drill down)
  const onEnterNode = (node) => {
    const { name, children } = pickNodeShape(node);
    setBrowseStack(prev => [...prev, browseCurrent]);
    setBrowseCurrent(children || []);
    setBrowsePath(prev => [...prev, name]);
  };

  // Back to parent
  const onBrowseBack = () => {
    if (browseStack.length === 0) return;
    const prev = [...browseStack];
    const last = prev.pop();
    setBrowseStack(prev);
    setBrowseCurrent(last || []);
    setBrowsePath(prevPath => prevPath.slice(0, -1));
  };

  // Select current node (leaf or non-leaf — your call)
  const onSelectNode = (node) => {
    const { id, name } = pickNodeShape(node);
    if (!id || !name) return;
    const fullPath = buildFullPath(name);
    handleCategorySelect(activeTabKeyword, id, fullPath);
    setBrowseOpen(false);
  };

  const searchCategories = useCallback(
    async (searchTerm) => {
      if (!searchTerm || searchTerm.trim().length < 1) {
        setCategories([]);
        setLoadingCategories(false);
        return;
      }
      const searchText = searchTerm.trim();
      const cacheKey = searchText;

      if (categoryCache[cacheKey]) {
        setCategories(categoryCache[cacheKey]);
        setLoadingCategories(false);
        return;
      }

      setLoadingCategories(true);
      try {
        const response = await CategoryApiService.searchCategoriesByKeyword(searchTerm);
        let formatted = [];
        if (response.status?.code === 200 && response.data) {
          formatted = response.data.map(category => ({
            id: category.c_id || category.id,
            name: category.c_name || category.name,
            path: category.path,
            searchText: normalizeText(category.path)
          }));
        }
        const tokens = tokenize(searchTerm);
        const fallbackMatches = allCategories.filter(c =>
          tokens.every(t => c.searchText.includes(t))
        );

        const merged = uniqueById([...(formatted || []), ...fallbackMatches]);

        setCategoryCache(prev => ({ ...prev, [cacheKey]: merged }));
        setCategories(merged);
      } catch (e) {
        console.error('Error searching categories:', e);
        const tokens = tokenize(searchTerm);
        const fallbackMatches = allCategories.filter(c =>
          tokens.every(t => c.searchText.includes(t))
        );
        setCategories(fallbackMatches);
      } finally {
        setLoadingCategories(false);
      }
    },
    [categoryCache, allCategories]
  );

  const handleCategorySelect = (keyword, categoryId, categoryName) => {
    const fullyApproved = isKeywordFullyApproved(keyword);
    if (fullyApproved) {
      alert('Category already mapped for these products.');
      return;
    }

    setKeywordCategories(prev => ({
      ...prev,
      [keyword]: { categoryId, categoryName }
    }));

    // Remove from skipped keywords if category is selected
    setSkippedKeywords(prev => {
      const newSet = new Set(prev);
      newSet.delete(keyword);
      return newSet;
    });
  };

  const handleSkipCategory = (keyword) => {
    const fullyApproved = isKeywordFullyApproved(keyword);
    if (fullyApproved) {
      alert('Category already mapped for these products.');
      return;
    }

    // Toggle skip status
    setSkippedKeywords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(keyword)) {
        // Unskip - remove from skipped
        newSet.delete(keyword);
      } else {
        // Skip - add to skipped and clear category selection
        newSet.add(keyword);
        setKeywordCategories(prevCat => {
          const newState = { ...prevCat };
          delete newState[keyword];
          return newState;
        });
      }
      return newSet;
    });
  };

  const handleRemoveProduct = (keyword, productId) => {
    removeProductFromStorage(keyword, productId);

    setLocalProductsByKeyword(prev => {
      const newState = { ...prev };
      if (newState[keyword]) {
        newState[keyword] = newState[keyword].filter(p => (p.sku || p.offerId) !== productId);
        if (newState[keyword].length === 0) delete newState[keyword];
      }
      const keys = Object.keys(newState);
      if (!keys.includes(activeTabKeyword)) setActiveTabKeyword(keys[0] || '');
      if (onProductRemove) onProductRemove(keyword, productId, newState);
      return newState;
    });
  };

  const handleRemoveKeywordGroup = (keyword) => {
    const products = localProductsByKeyword[keyword] || [];
    products.forEach(product => {
      const productId = product.sku || product.offerId;
      removeProductFromStorage(keyword, productId);
    });

    setLocalProductsByKeyword(prev => {
      const newState = { ...prev };
      delete newState[keyword];
      const keys = Object.keys(newState);
      if (keyword === activeTabKeyword) setActiveTabKeyword(keys[0] || '');
      if (onProductRemove) onProductRemove(keyword, null, newState);
      return newState;
    });

    setKeywordCategories(prev => {
      const newState = { ...prev };
      delete newState[keyword];
      return newState;
    });
  };

  const handleSubscribeNow = async () => {
    const groups = Object.keys(localProductsByKeyword);
    if (groups.length === 0) {
      alert('No products selected for subscription.');
      return;
    }

    // UI feedback start NOW
    setSubmitting(true);
    setDescriptionProgress({ current: 0, total: 0 });

    // validate category selection (allow skipped keywords)
    const missing = groups.filter(keyword => {
      const fullyApproved = isKeywordFullyApproved(keyword);
      if (fullyApproved) return false;
      // Allow if category is selected OR keyword is skipped
      if (skippedKeywords.has(keyword)) return false;
      return !keywordCategories[keyword]?.categoryId;
    });

    if (missing.length > 0) {
      alert(`Please select categories or skip for: ${missing.join(', ')}`);
      setSubmitting(false);
      return;
    }

    // Generate descriptions for all products before subscribing
    const productsWithDescriptions = [];
    let descriptionErrors = [];

    // Calculate total products for progress tracking
    const totalProducts = groups.reduce((total, keyword) => {
      return total + (localProductsByKeyword[keyword]?.length || 0);
    }, 0);

    setDescriptionProgress({ current: 0, total: totalProducts });
    let currentProgress = 0;

    for (const keyword of groups) {
      const items = localProductsByKeyword[keyword];
      const keywordCat = keywordCategories[keyword];
      const isSkipped = skippedKeywords.has(keyword);

      for (const product of items) {
        try {
          currentProgress++;
          setDescriptionProgress({ current: currentProgress, total: totalProducts });

          const productId = product.sku || product.offerId;
          const approvedAlready = isSkuApproved(productId);

          const aiCategoryName = approvedAlready
            ? (skuMeta[productId]?.categoryPath || '')
            : (isSkipped ? '' : (keywordCat?.categoryName || ''));

          const productName = product.subjectTrans || product.title || 'Product';

          // optional external description call
          try {
            const baseUrl = process.env.REACT_APP_APIUrl1688;
            const token = await fetchToken();
            const testResponse = await fetch(`${baseUrl}/generate-description`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-API-Token': token
              },
              body: JSON.stringify({
                productName: productName,
                targetLanguage: 'en',
                productCategory: aiCategoryName
              })
            });
            const testData = await testResponse.json();
            console.log('Direct API test response:', testData);
          } catch (testError) {
            console.error('Direct API test failed:', testError);
          }

          // Generate description using the API
          let generatedDescription = '';
          // Use keyword when category is skipped, otherwise use aiCategoryName
          const categoryForDescription = isSkipped ? keyword : (aiCategoryName || '');
          if (categoryForDescription) {
            const descriptionResponse = await SubscriptionApiService.generateDescription(
              productName,
              categoryForDescription,
              'en'
            );

            console.log('Description API response:', descriptionResponse);

            // let generatedDescription = '';
            if (descriptionResponse && descriptionResponse.result?.success) {
              const descriptionArray = descriptionResponse.result.result;
              if (Array.isArray(descriptionArray) && descriptionArray.length > 0) {
                generatedDescription = descriptionArray.join(' ');
              }
            } else if (descriptionResponse && descriptionResponse.description) {
              generatedDescription = descriptionResponse.description;
            }
          }

          const finalCategoryId = approvedAlready
            ? null
            : (isSkipped ? null : (keywordCat?.categoryId || null));

          const originalData = product.detailed_info || product.originalData || product;
          console.log('Original data for product:', productId, 'has data:', !!originalData);

          productsWithDescriptions.push({
            product_id: productId,
            keyword,
            category_id: finalCategoryId,
            generated_description: generatedDescription,
            sp: getDsFinalPriceNumber(product),
            alibaba_original_data: originalData
          });
        } catch (error) {
          console.error('Error generating description for product:', product.title, error);
          descriptionErrors.push(product.title || product.sku || product.offerId);

          const productId = product.sku || product.offerId;
          const approvedAlready = isSkuApproved(productId);
          const isSkipped = skippedKeywords.has(keyword);

          const fallbackCategoryId = approvedAlready
            ? null
            : (isSkipped ? null : (keywordCat?.categoryId || null));


          const sp = getDsFinalPriceNumber(product);

          const originalData = product.detailed_info || product.originalData || product;
          console.log('Original data for product (fallback):', productId, 'has data:', !!originalData);

          productsWithDescriptions.push({
            product_id: productId,
            keyword,
            category_id: fallbackCategoryId,
            generated_description: '',
            sp: (typeof sp === 'number' && !Number.isNaN(sp)) ? sp : null,
            alibaba_original_data: originalData
          });

          currentProgress++;
          setDescriptionProgress({ current: currentProgress, total: totalProducts });
        }
      }
    }

    // hand off payload to parent
    onSubscribe({ products: productsWithDescriptions });

    // end UI loading state immediately after handoff
    setSubmitting(false);
    setDescriptionProgress({ current: 0, total: 0 });

    // Show warning if some descriptions failed to generate
    if (descriptionErrors.length > 0) {
      console.warn('Failed to generate descriptions for:', descriptionErrors);
    }
  };

  const getTotalProductCount = () =>
    Object.values(localProductsByKeyword).reduce((t, products) => t + products.length, 0);

  const isSubscribeDisabled = () => {
    const groups = Object.keys(localProductsByKeyword);
    if (groups.length === 0) return true;

    for (const keyword of groups) {
      const fullyApproved = isKeywordFullyApproved(keyword);
      if (!fullyApproved) {
        // Allow if category is selected OR keyword is skipped
        if (!keywordCategories[keyword]?.categoryId && !skippedKeywords.has(keyword)) {
          return true;
        }
      }
    }
    return false;
  };

  const truncateText = (text, maxLength = 40) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Horizontal scroll helper
  const scrollProducts = (dir = 1) => {
    const node = scrollerRef.current;
    if (!node) return;
    const card = node.querySelector('.product-item');
    const step = card ? card.getBoundingClientRect().width + 16 : 300;
    node.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  if (!show) return null;

  const activeProducts = activeTabKeyword ? (localProductsByKeyword[activeTabKeyword] || []) : [];
  const activeCategoryData = activeTabKeyword ? keywordCategories[activeTabKeyword] : undefined;
  const fullyApprovedActive = activeTabKeyword
    ? isKeywordFullyApproved(activeTabKeyword)
    : false;

  return (
    <div className="modern-modal-overlay" onClick={onHide}>
      <div className="modern-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modern-modal-header">
          <div className="modal-header-content">
            <h2 className="modal-title">Subscribe Products</h2>
            <p className="modal-subtitle">
              {getTotalProductCount()} products selected across {keywordGroups.length} search{keywordGroups.length > 1 ? 'es' : ''}
            </p>
          </div>
          <button className="modern-close-btn" onClick={onHide} disabled={isLoading || submitting} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* Tabs */}
        <div className="tabs-container" role="tablist" aria-label="Keyword Tabs">
          {keywordGroups.length === 0 ? (
            <div className="tabs-empty-placeholder">No searches</div>
          ) : (
            keywordGroups.map((kw) => {
              const count = (localProductsByKeyword[kw] || []).length;
              const isActive = kw === activeTabKeyword;

              const kwFullyApproved = isKeywordFullyApproved(kw);
              const hasCategorySelection = !!keywordCategories[kw]?.categoryId;
              const isSkipped = skippedKeywords.has(kw);
              const isChecked = kwFullyApproved || hasCategorySelection || isSkipped;
              const isUnchecked = !isChecked;

              return (
                <button
                  key={kw}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`panel-${kw}`}
                  id={`tab-${kw}`}
                  className={`tab-item ${isActive ? 'tab-item-active' : ''} ${isChecked ? 'tab-item-checked' : ''} ${isUnchecked ? 'tab-item-unchecked' : ''}`}
                  onClick={() => setActiveTabKeyword(kw)}
                  disabled={isLoading || submitting}
                  title={`Show products for "${kw}"`}
                >
                  {isChecked ? (
                    <FaCheckCircle className="tab-tick" aria-hidden="true" />
                  ) : (
                    <FaExclamationCircle className="tab-warn" aria-hidden="true" />
                  )}
                  <span className="tab-title">"{kw}"</span>
                  <span className="tab-badge">{count}</span>
                </button>
              );
            })
          )}
        </div>

        {/* Body */}
        <div className="modern-modal-body">
          {keywordGroups.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <h3>No products selected</h3>
              <p>Select some products first to subscribe them.</p>
            </div>
          ) : (
            <div
              id={`panel-${activeTabKeyword}`}
              role="tabpanel"
              aria-labelledby={`tab-${activeTabKeyword}`}
            >
              {/* Keyword header */}
              <div className="keyword-header">
                <div className="keyword-info">
                  <h3 className="keyword-title">"{activeTabKeyword}"</h3>
                  <span className="product-count-badge">
                    {activeProducts.length} product{activeProducts.length > 1 ? 's' : ''}
                  </span>
                </div>
                <button
                  className="remove-group-btn"
                  onClick={() => handleRemoveKeywordGroup(activeTabKeyword)}
                  title={`Remove all products for "${activeTabKeyword}"`}
                  disabled={isLoading || submitting}
                >
                  <FaTrash />
                </button>
              </div>

              {/* Category selection */}
              <div className="category-section">
                <label className="category-label">
                  Select Category for "{activeTabKeyword}" products:
                </label>
                <p className="category-instruction">
                  {fullyApprovedActive
                    ? 'These products are already category-approved. You cannot change their category.'
                    : skippedKeywords.has(activeTabKeyword)
                      ? 'Category mapping skipped. You can subscribe without mapping a category.'
                      : 'Search and select the most appropriate category for these products, or skip to subscribe without category mapping.'}
                </p>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <CategoryDropdown
                      categories={categories}
                      selectedCategoryId={activeCategoryData?.categoryId || ''}
                      selectedCategoryPath={activeCategoryData?.categoryName || ''}
                      onCategorySelect={(categoryId, categoryName) =>
                        handleCategorySelect(activeTabKeyword, categoryId, categoryName)
                      }
                      onSearch={searchCategories}
                      loading={loadingCategories}
                      placeholder={
                        fullyApprovedActive
                          ? 'Category already mapped'
                          : skippedKeywords.has(activeTabKeyword)
                            ? 'Category skipped'
                            : 'Search and select a category...'
                      }
                      disabled={isLoading || submitting || fullyApprovedActive || skippedKeywords.has(activeTabKeyword)}
                      loadBrowseTree={async () => {
                        const res = await CategoryApiService.getCategories();
                        if (res?.status?.code === 200 && Array.isArray(res.data)) return res.data;
                        return [];
                      }}
                      onBrowse={openBrowse}
                    />
                  </div>
                  {!fullyApprovedActive && (
                    <button
                      type="button"
                      onClick={() => handleSkipCategory(activeTabKeyword)}
                      disabled={isLoading || submitting}
                      className={`skip-category-btn ${skippedKeywords.has(activeTabKeyword) ? 'skipped' : ''
                        }`}
                    >
                      {skippedKeywords.has(activeTabKeyword) ? '✓ Skipped (Click to Unskip)' : 'Skip Category'}
                    </button>
                  )}
                </div>

                {fullyApprovedActive && (
                  <p
                    className="category-locked-note"
                    style={{
                      fontSize: 12,
                      color: '#10b981',
                      marginTop: 6,
                      lineHeight: 1.4,
                    }}
                  >
                    Category already mapped for these products.
                  </p>
                )}
                {!fullyApprovedActive && skippedKeywords.has(activeTabKeyword) && (
                  <p
                    className="category-skipped-note"
                    style={{ fontSize: 12, color: '#f59e0b', marginTop: 6, lineHeight: 1.4, }}
                  >
                    Category mapping skipped. Products will be subscribed without category mapping.
                  </p>
                )}
              </div>

              {/* PRODUCTS — HORIZONTAL SCROLLER */}
              <div className="products-row-wrap">
                <button
                  type="button"
                  className="scroll-btn scroll-btn-left"
                  aria-label="Scroll left"
                  onClick={() => scrollProducts(-1)}
                  disabled={isLoading || submitting}
                >
                  ‹
                </button>

                <div className="products-scroller" ref={scrollerRef}>
                  {activeProducts.map((product, index) => {
                    const productId = product.sku || product.offerId;

                    // Use the exact same image loading logic as ProductCard component
                    const getImageUrl = () => {
                      let initialImageUrl;

                      // Check if product is from featured category (MongoDB)
                      const isFromFeaturedCategory = product.isFromFeaturedCategory || false;

                      if (isFromFeaturedCategory && product?.imgUrl?.length > 0) {
                        // Featured categories: find first actual image URL (skip route paths)
                        const findValidImageUrl = (urlArray) => {
                          for (let i = 0; i < urlArray.length; i++) {
                            const url = urlArray[i];
                            if (!url || typeof url !== 'string') continue;

                            const urlLower = url.toLowerCase().trim();
                            // Skip if it's a route path (contains /fc/, /product, /collection but no image extension)
                            const hasRoutePattern = urlLower.includes('/fc/') || urlLower.includes('/product') || urlLower.includes('/collection');
                            const hasImageExtension = /\.(jpg|jpeg|png|gif|webp|svg)(\?|$|#)/i.test(urlLower);
                            const isImageCDN = urlLower.includes('alicdn.com') || urlLower.includes('amazonaws.com') || urlLower.includes('cloudfront.net') || urlLower.includes('s3.');

                            // If it has route pattern but no image extension/CDN, skip it
                            if (hasRoutePattern && !hasImageExtension && !isImageCDN) continue;

                            // If it has image extension or is from CDN, use it
                            if (hasImageExtension || isImageCDN) return url;
                          }
                          return null;
                        };

                        const validUrl = findValidImageUrl(product.imgUrl);
                        initialImageUrl = validUrl ? normalizeUrl(validUrl) : Logo;
                      } else {
                        // Alibaba products: use imgUrl[0]
                        initialImageUrl = normalizeUrl(product?.imgUrl?.[0]) || Logo;
                      }

                      // Append 200x200 size for performance
                      if (initialImageUrl && initialImageUrl !== Logo) {
                        initialImageUrl = appendSizeToImageUrl(initialImageUrl, '200x200');
                      }

                      return initialImageUrl;
                    };

                    const imageUrl = getImageUrl();

                    return (
                      <div key={index} className="product-item">
                        <img
                          src={imageUrl}
                          alt="Product"
                          className="product-image"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            // Prevent infinite loop by checking if we're already showing the placeholder
                            if (e.currentTarget.src.indexOf('logo_no_image.png') === -1) {
                              e.currentTarget.src = '/assets/images/common/logo_no_image.png';
                            }
                          }}
                        />
                        <div className="product-details">
                          <h4
                            className="product-name"
                            title={product.subjectTrans || product.title}
                          >
                            {truncateText(product.subjectTrans || product.title)}
                          </h4>

                          {/* <p className="product-sku">SKU: {productId || 'N/A'}</p> */}

                          <p className="product-sku">
                            SKU: {productId || 'N/A'}
                            {(() => {
                              const dp = getDisplayPriceForProduct(product);
                              return dp ? ` | Price : ${dp}` : '';
                            })()}
                            {(() => {
                              const vc = getVariantCount(product);
                              return vc != null ? ` | Variants : ${vc}` : '';
                            })()}
                          </p>

                          {skuMeta[productId]?.is_category_approved &&
                            skuMeta[productId]?.categoryPath ? (
                            <p className="product-category-path">
                              Category Name : {skuMeta[productId].categoryPath}
                            </p>
                          ) : null}
                        </div>

                        <button
                          className="remove-product-btn"
                          onClick={() => handleRemoveProduct(activeTabKeyword, productId)}
                          title="Remove this product"
                          disabled={isLoading || submitting}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="scroll-btn scroll-btn-right"
                  aria-label="Scroll right"
                  onClick={() => scrollProducts(1)}
                  disabled={isLoading || submitting}
                >
                  ›
                </button>
              </div>
              {/* /PRODUCTS */}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modern-modal-footer">
          <button
            className="btn-secondary"
            onClick={onHide}
            disabled={isLoading || submitting}
          >
            Cancel
          </button>

          <button
            className="btn-primary"
            onClick={handleSubscribeNow}
            disabled={submitting || isLoading || isSubscribeDisabled()}
          >
            {(submitting || isLoading) ? (
              <>
                <div className="btn-spinner"></div>
                {descriptionProgress.total > 0 ? (
                  `Generating descriptions (${descriptionProgress.current}/${descriptionProgress.total})...`
                ) : (
                  'Subscribing products...'
                )}
              </>
            ) : (
              `Subscribe All (${getTotalProductCount()} products)`
            )}
          </button>
        </div>

        {/* Browse Tree Modal */}
        {browseOpen && (
          <div
            className="browse-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={() => setBrowseOpen(false)}
          >
            <div
              className="browse-modal"
              style={{
                width: 'min(720px, 95vw)',
                maxHeight: '80vh',
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  borderBottom: '1px solid #eee'
                }}
              >
                <button
                  type="button"
                  onClick={() => (browseStack.length ? onBrowseBack() : setBrowseOpen(false))}
                  style={{
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    borderRadius: 8,
                    padding: '6px 10px',
                    cursor: 'pointer'
                  }}
                  aria-label={browseStack.length ? 'Back' : 'Close'}
                >
                  {browseStack.length ? '← Back' : '✕'}
                </button>
                <div style={{ fontWeight: 600, fontSize: 16 }}>
                  Browse Categories
                </div>
                <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 13 }}>
                  {browsePath.length ? browsePath.join(' > ') : 'All Categories'}
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: 12, overflow: 'auto' }}>
                {browseLoading ? (
                  <div style={{ padding: 24 }}>Loading categories…</div>
                ) : browseError ? (
                  <div style={{ padding: 24, color: '#b91c1c' }}>{browseError}</div>
                ) : browseCurrent.length === 0 ? (
                  <div style={{ padding: 24 }}>No categories here.</div>
                ) : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {browseCurrent.map((node, idx) => {
                      const { name, children, id } = pickNodeShape(node);
                      const hasChildren = Array.isArray(children) && children.length > 0;
                      return (
                        <li
                          key={id || idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '10px 12px',
                            borderBottom: '1px solid #f3f4f6'
                          }}
                        >
                          <div style={{ flex: 1, fontSize: 14 }}>{name}</div>
                          {hasChildren ? (
                            <button
                              type="button"
                              onClick={() => onEnterNode(node)}
                              style={{
                                border: '1px solid #e5e7eb',
                                background: '#fff',
                                borderRadius: 8,
                                padding: '6px 10px',
                                fontSize: 13,
                                cursor: 'pointer'
                              }}
                              title="View sub-categories"
                              aria-label="View sub-categories"
                            >
                              View →
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => onSelectNode(node)}
                              style={{
                                border: '1px solid #10b981',
                                background: '#10b981',
                                color: '#fff',
                                borderRadius: 8,
                                padding: '6px 10px',
                                fontSize: 13,
                                cursor: 'pointer'
                              }}
                              title="Select this category"
                              aria-label="Select this category"
                            >
                              Select
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
        {/* -------- /BROWSE TREE MODAL -------- */}
      </div>
    </div>
  );
};

export default SubscriptionModal;
